<section>
  @if (!loading) {
    <div class="lead-magnet-form">
      @if (step === 1) {
        <h2 class="form-title">Set Up your Lead Magnet</h2>
        <br>
          <p class="form-description">
            Tell us about your book and we'll build your lead magnet page for you.
            Fill out the fields below and upload the lead magnet file readers will receive.
            You'll be able to preview your landing page on the next step.
          </p>
          <form [formGroup]="form">
            <div class="form-body">
              <div class="cover-column">
                <div class="cover-preview">
                  @if (form.get('landingPage.cover').value) {
                    <img
                      [src]="form.get('landingPage.cover').value" alt="Book cover" />
                  } @else {
                    <div class="cover-placeholder">
                      <img id="preview-cover"
                        src="/assets/img/defaults/default_book_image_400w_simple.jpg">
                      </div>
                    }
                    @if (coverUploading) {
                      <app-spinner [zoom]="0.4" label="Uploading"></app-spinner>
                    }
                  </div>
                  <button type="button" class="btn btn-outline cover-upload-btn"
                    [class]="{invalid: isFieldInvalid('landingPage.cover')}" (click)="triggerCoverUpload()"
                    [disabled]="coverUploading">
                  Upload Book Cover
                </button>
                <input #coverInput type="file" accept=".jpg,.jpeg,.gif,.png" class="hidden-input"
                  (change)="onCoverFileChange($event)" />
                <p class="upload-ratio">6×9 Ratio</p>
                <p class="upload-hint">Accepted file types: jpg, jpeg, gif, or png</p>
                @if (coverUploadError) {
                  <p class="upload-error">{{ coverUploadError }}</p>
                }
                @if (isFieldInvalid('landingPage.cover')) {
                  <p class="field-error">Please upload a book cover.
                  </p>
                }
              </div>
              <div class="fields-column">
                <div class="field-group">
                  <label class="field-label">Type of Lead Magnet</label>
                  <div class="type-selector">
                    @for (option of assetTypeOptions; track option.type) {
                      <button type="button" class="type-btn"
                        [class.selected]="option.type === selectedAssetType" (click)="selectAssetType(option.type)">
                        {{ option.name }}
                      </button>
                    }
                  </div>
                </div>
                <div class="flex">
                  <input type="checkbox" formControlName="kdpSelectTerms" />
                  <label class="field-label" style="color: #787878; font-size: 12px;">
                    I understand that offering a {{ selectedTypeLabel | lowercase }} for free may violate KDP Select's terms of service.
                    By checking this box, I confirm that this content is not enrolled in KDP Select.
                  </label>
                </div>
                <div class="field-group" formGroupName="landingPage">
                  <label class="field-label" for="title">Book Title</label>
                  <input id="title" type="text" class="text-input" formControlName="title"
                    placeholder="The River Between" />
                  @if (isFieldInvalid('landingPage.title')) {
                    <p class="field-error">This field is
                    required.</p>
                  }
                </div>
                <div class="field-group" formGroupName="landingPage">
                  <label class="field-label" for="author">Author</label>
                  <input id="author" type="text" class="text-input" formControlName="author"
                    placeholder="John Doe" />
                  @if (isFieldInvalid('landingPage.author')) {
                    <p class="field-error">This field is
                    required.</p>
                  }
                </div>
                <div class="field-group">
                  <label class="field-label" for="genre">Genre</label>
                  <select id="genre" class="text-input" formControlName="genre">
                    <option value="" disabled>-- Select Genre --</option>
                    @for (genre of genreOptions; track genre) {
                      <option [value]="genre">{{ genre }}</option>
                    }
                  </select>
                  @if (isFieldInvalid('genre')) {
                    <p class="field-error">This field is
                    required.</p>
                  }
                </div>
                <div class="field-group" formGroupName="landingPage">
                  <label class="field-label" for="headline">Landing Page Headline</label>
                  <input id="headline" type="text" class="text-input" formControlName="headline"
                    placeholder="Get your free copy!" />
                  <a href="javascript::void(0)" #headlineToggle
                  (click)="showHeadlineDetails = !showHeadlineDetails">Headline Details</a>
                  @if (showHeadlineDetails) {
                    <div class="card link-options flex flex-direction-column gap-10" #headlineTooltip>
                      <p>Clear beats clever here. Tell readers exactly what they're getting and that it's
                        free.
                        Example: “Get the free ebook from Jane Austen.” Save the flair for your tagline.
                      </p>
                      @if (guideLink) {
                        <div>
                          <a [href]="guideLink">Check out our guide to creating a great Lead Magnet</a>
                        </div>
                      }
                      <div class="bold-underline">
                        <a (click)="writeItForMe('headline')">Want us to handle this? Click here and
                        we'll write the headline for you</a>
                      </div>
                    </div>
                  }
                  @if (isFieldInvalid('landingPage.headline')) {
                    <p class="field-error">This field is
                    required.</p>
                  }
                </div>
                <div class="field-group" formGroupName="landingPage">
                  <label class="field-label" for="tagline">Landing Page Tagline</label>
                  <textarea id="tagline" class="textarea-input" rows="4" formControlName="tagline"
                  placeholder="Prepare for a charming and suspenseful ride..."></textarea>
                  <a href="javascript::void(0)" #taglineToggle
                  (click)="showTaglineDetails = !showTaglineDetails">Tagline Details</a>
                  @if (showTaglineDetails) {
                    <div class="card link-options flex flex-direction-column gap-10" #taglineTooltip>
                      <p>Make it a hook, not a label. One short line that shows off your genre and voice.
                        Don't repeat your book title (it's already on the page). Example: “A story you
                      won't forget”</p>
                      @if (guideLink) {
                        <div>
                          <a [href]="guideLink">Check out our guide to creating a great Lead Magnet</a>
                        </div>
                      }
                      <div class="bold-underline">
                        <a (click)="writeItForMe('tagline')">Want us to handle this? Click here and
                        we'll write the tagline for you</a>
                      </div>
                    </div>
                  }
                  @if (isFieldInvalid('landingPage.tagline')) {
                    <p class="field-error">This field is
                    required.</p>
                  }
                </div>
                <div class="field-group" formGroupName="landingPage">
                  <label class="field-label" for="description">Book Description (for promotion)</label>
                  <textarea id="description" class="textarea-input" rows="6" formControlName="description"
                  placeholder="Write a compelling description of your book..."></textarea>
                  @if (isFieldInvalid('landingPage.description')) {
                    <p class="field-error">This field is
                    required.</p>
                  }
                </div>
                <div class="field-group">
                  <div class="asset-upload-row" [class]="{invalid: isFieldInvalid('asset.link')}">
                    @if (hasAsset === false) {
                      <div class="asset-upload-left">
                        @if (assetUploading) {
                          <app-spinner [zoom]="0.3"
                          [label]="assetValidating ? 'Checking file' : 'Uploading'"></app-spinner>
                        }
                        @if (!assetUploading) {
                          <span>Upload Lead (book, chapter, short story)</span>
                        }
                        <p class="upload-hint">Accepted file types: pdf, epub</p>
                        @if (assetUploadError) {
                          <p class="upload-error">{{ assetUploadError }}</p>
                        }
                      </div>
                    } @else {
                      <div>{{assetName}}</div>
                    }
                    @if (hasAsset === false) {
                      <button type="button" class="btn btn-primary outline"
                        (click)="triggerAssetUpload()" [disabled]="assetUploading">
                        Upload Lead Magnet
                      </button>
                    }
                    @if (hasAsset) {
                      <button type="button" class="btn btn-danger outline"
                        (click)="removeAsset()">
                        Remove
                      </button>
                    }
                    <input #assetInput type="file" accept=".pdf,.epub" class="hidden-input"
                      (change)="onAssetFileChange($event)" />
                  </div>
                  @if (isFieldInvalid('asset.link')) {
                    <p class="field-error">Please upload a lead magnet
                    file.</p>
                  }
                </div>
                <div class="flex" [class]="{'invalid': isFieldInvalid('rightsCertification')}">
                  <input type="checkbox" formControlName="rightsCertification" />
                  <label class="field-label" style="color: #787878; font-size: 12px;">
                    I certify that I own or control all rights necessary to promote this book,
                    and that its content does not infringe any third party's copyright, trademark,
                    or other rights.
                  </label>
                </div>
              </div>
            </div>
          </form>
          @if (showPurchaserInfo) {
            <form [formGroup]="purchaserInfoForm">
              <h4></h4>
              <div class="form-body">
                <div class="blank-column"></div>
                <div class="fields-column">
                  <div class="field-group">
                    <label class="field-label" for="email">Email</label>
                    <input id="email" class="textarea-input" formControlName="email" placeholder="Email">
                    @if (isFieldInvalid('email')) {
                      <p class="field-error">This field is required.</p>
                    }
                  </div>
                  <div class="flex">
                    <div class="field-group flex-grow-1">
                      <label class="field-label" for="firstName">First Name</label>
                      <input id="firstName" class="textarea-input" formControlName="firstName"
                        placeholder="First Name">
                      @if (isFieldInvalid('firstName')) {
                        <p class="field-error">This field is required.</p>
                      }
                    </div>
                    <div class="field-group flex-grow-1">
                      <label class="field-label" for="lastName">Last Name</label>
                      <input id="lastName" class="textarea-input" formControlName="lastName"
                        placeholder="Last Name">
                      @if (isFieldInvalid('lastName')) {
                        <p class="field-error">This field is required.</p>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </form>
          }
          <!-- <div class="landing-url-row" *ngIf="landingPageUrl">
          <span class="url-icon">&#128279;</span>
          <a [href]="'https://' + landingPageUrl" target="_blank" class="landing-url">{{ landingPageUrl }}</a>
          <p class="url-hint">This is the URL where your generated landing page will be hosted.</p>
        </div> -->
        @if (saveError) {
          <p class="save-error">{{ saveError }}</p>
        }
        <div class="form-footer">
          <button type="button" class="btn btn-outline" (click)="onPreview()">Preview Landing Page</button>
          <button type="button" class="btn btn-primary" (click)="onStep1Continue()">Continue &rsaquo;</button>
        </div>
      }
      @if (step === 2) {
        <h2 class="form-title">Choose how to run your campaign</h2>
        <p class="form-description">
          With Reader Sharing, new readers get the option to also subscribe to
          Bargain Booksy — and you save {{ sharedDiscount }}% on your campaign.
          You'll still receive every lead for your author list. Prefer to keep it exclusive? Pay full price and every signup is yours alone.
        </p>
        <div class="funding-options">
          <div class="funding-card" [class.selected]="selectedFunding === 'STANDARD'"
            (click)="selectFunding('STANDARD')">
            <div class="funding-card-header">
              <span class="funding-card-title">Pay full price for the campaign</span>
              <span class="funding-radio" [class.active]="selectedFunding === 'STANDARD'"></span>
            </div>
            <p class="funding-card-desc">We'll run ads to your landing page to drive email signups directly to
            your list.</p>
            @if (standardMemberDiscountAmount <= 0) {
              <p class="funding-price">${{ fullPrice | number:'1.2-2' }}</p>
            }
            @if (standardMemberDiscountAmount > 0) {
              <div class="funding-price-breakdown">
                <div class="funding-price-line">
                  <span>Subtotal</span>
                  <span>${{ fullPrice | number:'1.2-2' }}</span>
                </div>
                <div class="funding-price-line funding-price-line--discount">
                  <span>Member Discount</span>
                  <span>-${{ standardMemberDiscountAmount | number:'1.2-2' }}</span>
                </div>
                <div class="funding-price-line funding-price-line--total">
                  <span>Total</span>
                  <span class="funding-price">${{ standardMemberPrice | number:'1.2-2' }}</span>
                </div>
              </div>
            }
          </div>
          <div class="funding-card" [class.selected]="selectedFunding === 'SHARED'"
            (click)="selectFunding('SHARED')">
            <div class="funding-card-header">
              <span class="funding-card-title">Get {{ sharedDiscount }}% off with Reader Sharing</span>
              <span class="funding-radio" [class.active]="selectedFunding === 'SHARED'"></span>
            </div>
            <p class="funding-card-desc">Give new readers the option to also subscribe to {{ hostSite }}.
            You'll still receive every signup generated for your author list.</p>
            @if (sharedMemberDiscountAmount <= 0) {
              <div class="funding-price-row">
                <p class="funding-price">${{ sharedPrice | number:'1.2-2' }}</p>
                <span class="discount-badge">{{ sharedDiscount }}% OFF</span>
              </div>
            }
            @if (sharedMemberDiscountAmount > 0) {
              <div class="funding-price-breakdown">
                <div class="funding-price-line">
                  <span>Subtotal</span>
                  <div class="funding-price-row">
                    <span>${{ sharedPrice | number:'1.2-2' }}</span>
                    <span class="discount-badge">{{ sharedDiscount }}% OFF</span>
                  </div>
                </div>
                <div class="funding-price-line funding-price-line--discount">
                  <span>Member Discount</span>
                  <span>-${{ sharedMemberDiscountAmount | number:'1.2-2' }}</span>
                </div>
                <div class="funding-price-line funding-price-line--total">
                  <span>Total</span>
                  <span class="funding-price">${{ sharedMemberPrice | number:'1.2-2' }}</span>
                </div>
              </div>
            }
            <a class="explain-link" (click)="$event.preventDefault(); onExplainClick()">Explain this to me</a>
          </div>
        </div>
        @if (saveError) {
          <p class="save-error">{{ saveError }}</p>
        }
        <div class="form-footer form-footer--spaced">
          <button type="button" class="btn btn-outline" (click)="onBack()">Back to Campaign Set-Up</button>
          <div>
            <button type="button" class="btn btn-outline" style="margin-right: 12px;"
            (click)="onPreview()">Preview Landing Page</button>
            <button type="button" class="btn btn-primary" (click)="onContinue()" [disabled]="saving">
              <span>{{ saving ? 'Saving...' : 'Continue &rsaquo;' }}</span>
            </button>
          </div>
        </div>
      }
    </div>
  } @else {
    <app-spinner label="Loading"></app-spinner>
  }


  <app-modal [id]="explainModalId" [maskClose]="true">
    <!-- Margin here makes it appear more centered on content - Less jarring -->
    <aside class="modal" style="margin-left: 250px">
      <div class="modal-body">
        <div class="header">
          <div class="title-text">How does “share readers for a discount” work?</div>
          <div class="button regular close" (click)="modalService.close(explainModalId)"><span
          class="icon">&#x00D7;</span></div>
        </div>
        <div>
          <hr>
            <p>
              Two ways to run your Lead Magnet
            </p>
            <p>When you book a Lead Magnet, you choose how you'd like to pay:</p>
            <ul>
              <li>Pay full price and keep the signups all to yourself.</li>
              <li>Pay a reduced rate by letting readers opt into {{ hostSite }}'s newsletter alongside yours
              </li>
            </ul>
            <p>
              If you choose the reduced rate, readers will see the option to join both your list and Bargain
              Booksy's when they sign up. It's one extra checkbox on their signup form, and it's not required.
              Either way, you still grow your own list!
            </p>
          </div>
        </div>
      </aside>
    </app-modal>
    <app-new-error-modal [modalId]="validationErrorModalId" [options]="validationErrorOptions">
      <ul class="validation-issues">
        @for (issue of assetValidationErrors; track issue.title) {
          <li class="validation-issue">
            <strong>{{ issue.title }}</strong>
            <p>{{ issue.description }}</p>
            @if (issue.files.length) {
              <ul class="validation-files">
                @for (file of issue.files; track $index) {
                  <li><code>{{ file }}</code></li>
                }
              </ul>
            }
            @if (issue.messages.length) {
              <ul class="validation-messages">
                @for (message of issue.messages; track $index) {
                  <li>{{ message }}</li>
                }
              </ul>
            }
          </li>
        }
      </ul>
      <div class="validation-fix-hint">
        How do I fix these?
        <app-tooltip-card label="?" [text]="validationFixHint"></app-tooltip-card>
      </div>
    </app-new-error-modal>
  </section>
